import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { getSettings, sendEnquiry } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const SUBJECTS = ["General enquiry", "Bulk / Trade enquiry", "Order support", "Distributorship"];

const inputCls =
  "mt-1.5 w-full rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
  const [sending, setSending] = useState(false);
  const [contact, setContact] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    getSettings()
      .then(setContact)
      .catch(() => {});
  }, []);

  const address = contact?.contact_address || "230/4 Nana Peth, Pragati Villa, Pune 411002, Maharashtra, India";
  const phone = contact?.contact_phone || "+91 99607 49074";
  const mail = contact?.contact_email || "info@sbkmanufacturing.com";
  const hours = contact?.contact_hours || "";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendEnquiry(form);
      toast.success("Message sent — we'll reply within 1 business day");
      setForm({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-5 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-2"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary mb-4">03 · Say hello</p>
          <h2 className="font-serif text-3xl md:text-5xl font-medium tracking-tight leading-tight">
            Let's put paper <span className="italic text-primary">in your hands.</span>
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Questions about a product, a bulk order or stocking Classina? Drop us a line — we respond within
            one business day.
          </p>
          <div className="mt-10 space-y-5 text-sm">
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <p data-testid="contact-address" className="text-muted-foreground whitespace-pre-line">{address}</p>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-primary shrink-0" />
              <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} data-testid="contact-phone" className="text-muted-foreground hover:text-foreground transition-colors">{phone}</a>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-primary shrink-0" />
              <a href={`mailto:${mail}`} data-testid="contact-email" className="text-muted-foreground hover:text-foreground transition-colors">{mail}</a>
            </div>
            {hours && (
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-primary shrink-0" />
                <p data-testid="contact-hours" className="text-muted-foreground">{hours}</p>
              </div>
            )}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-3 bg-white/70 backdrop-blur-sm border border-border/60 rounded-2xl p-8 md:p-10 shadow-sm"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Your name</span>
              <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Asha Patil" className={inputCls} />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Email</span>
              <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Phone (optional)</span>
              <input data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" className={inputCls} />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Subject</span>
              <select data-testid="contact-subject-select" value={form.subject} onChange={set("subject")} className={inputCls}>
                {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <label className="block mt-4">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Message</span>
            <textarea data-testid="contact-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder="Tell us what you need…" className={`${inputCls} resize-none`} />
          </label>
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="mt-6 rounded-full bg-primary text-primary-foreground px-8 py-3.5 text-sm font-bold inline-flex items-center gap-2 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending…" : "Send message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
